// frontend/src/components/company/DriverDetailModal.jsx
import { useState, useEffect } from 'react';
import { driverAPI } from '../../services/api';

const DriverDetailModal = ({ isOpen, driver, onClose, onEdit }) => {
  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [activeTab, setActiveTab] = useState('info'); // info, deliveries, stats
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState(null);

  // ============================================
  // FETCH DRIVER DETAILS
  // ============================================
  useEffect(() => {
    if (isOpen && driver) {
      fetchDriverDetails();
      if (activeTab === 'stats') {
        fetchDriverStats();
      }
    }
  }, [isOpen, driver, activeTab]);

  const fetchDriverDetails = async () => {
    try {
      setLoading(true);
      const response = await driverAPI.getById(driver.id);
      setDetailData(response.data.data);
    } catch (err) {
      console.error('❌ Fetch driver details error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverStats = async () => {
    try {
      setStatsLoading(true);
      const response = await driverAPI.getStats(driver.id);
      setStats(response.data.data);
    } catch (err) {
      console.error('❌ Fetch driver stats error:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  const getStatusBadge = (status) => {
    const badges = {
      available: { bg: 'bg-green-100', text: 'text-green-700', label: '🟢 Бэлэн' },
      busy: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '🟡 Завгүй' },
      offline: { bg: 'bg-gray-100', text: 'text-gray-700', label: '⚫ Offline' }
    };
    
    const badge = badges[status] || badges.offline;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getDeliveryStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Хүлээгдэж буй' },
      in_transit: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Хүргэж байгаа' },
      delivered: { bg: 'bg-green-100', text: 'text-green-700', label: 'Хүргэсэн' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Цуцлагдсан' },
      postponed: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Хойшлогдсон' }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <span key={i} className="text-yellow-400 text-xl">
            {i < fullStars ? '★' : (i === fullStars && hasHalfStar ? '⯨' : '☆')}
          </span>
        ))}
        <span className="ml-2 text-sm text-gray-600 font-medium">
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('mn-MN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !driver) return null;

    // ============================================
    // FIX: Nested response structure
    // ============================================
    const data = detailData?.driver || driver;
  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl transform transition-all max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  {data.driver_name?.charAt(0) || '👤'}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    {data.driver_name}
                    {data.is_active ? (
                      <span className="text-xs px-2 py-1 bg-green-500 text-white rounded-full">
                        ✓ Идэвхтэй
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-red-500 text-white rounded-full">
                        ✗ Идэвхгүй
                      </span>
                    )}
                  </h2>
                  <div className="flex items-center gap-3 mt-2">
                    <p className="text-blue-100 text-sm">📞 {data.phone}</p>
                    {data.email && (
                      <p className="text-blue-100 text-sm">📧 {data.email}</p>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 flex-shrink-0 bg-gray-50">
            <div className="flex gap-1 px-6">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'info'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                📋 Мэдээлэл
              </button>
              <button
                onClick={() => setActiveTab('deliveries')}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'deliveries'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                📦 Хүргэлтүүд
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'stats'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                📊 Статистик
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600"></div>
                  <p className="text-gray-600 mt-4">Уншиж байна...</p>
                </div>
              </div>
            ) : (
              <>
                {/* ============================================ */}
                {/* TAB 1: МЭДЭЭЛЭЛ */}
                {/* ============================================ */}
                {activeTab === 'info' && (
                  <div className="space-y-6">
                    {/* Status & Rating */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-bold text-green-900">Одоогийн статус</h3>
                          <span className="text-2xl">📍</span>
                        </div>
                        {getStatusBadge(data.status)}
                        {data.last_location_updated && (
                          <p className="text-xs text-green-700 mt-2">
                            Сүүлд шинэчлэгдсэн: {formatDate(data.last_location_updated)}
                          </p>
                        )}
                      </div>

                      <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5 border border-yellow-200">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-bold text-yellow-900">Үнэлгээ</h3>
                          <span className="text-2xl">⭐</span>
                        </div>
                        {renderStars(parseFloat(data.rating) || 0)}
                      </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                        <div className="text-3xl font-bold text-blue-600 mb-1">
                          {data.total_deliveries || 0}
                        </div>
                        <div className="text-xs text-gray-600 font-medium">Нийт хүргэлт</div>
                      </div>

                      <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                        <div className="text-3xl font-bold text-green-600 mb-1">
                          {data.successful_deliveries || 0}
                        </div>
                        <div className="text-xs text-gray-600 font-medium">Амжилттай</div>
                      </div>

                      <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                        <div className="text-3xl font-bold text-purple-600 mb-1">
                          {data.total_deliveries > 0 
                            ? Math.round((data.successful_deliveries / data.total_deliveries) * 100)
                            : 0}%
                        </div>
                        <div className="text-xs text-gray-600 font-medium">Амжилтын хувь</div>
                      </div>

                      <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                        <div className="text-3xl font-bold text-orange-600 mb-1">
                          {(data.total_deliveries || 0) - (data.successful_deliveries || 0)}
                        </div>
                        <div className="text-xs text-gray-600 font-medium">Бусад</div>
                      </div>
                    </div>

                    {/* Personal Info */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="text-lg">👤</span>
                        Хувийн мэдээлэл
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-600 font-medium block mb-1">Утас</label>
                          <p className="text-sm font-semibold text-gray-900">📞 {data.phone}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 font-medium block mb-1">И-мэйл</label>
                          <p className="text-sm font-semibold text-gray-900">{data.email || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 font-medium block mb-1">Үнэмлэхний дугаар</label>
                          <p className="text-sm font-semibold text-gray-900">{data.license_number || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 font-medium block mb-1">Бүртгэгдсэн огноо</label>
                          <p className="text-sm font-semibold text-gray-900">{formatDate(data.created_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Vehicle Info */}
                    {(data.vehicle_number || data.vehicle_type) && (
                      <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                        <h3 className="text-sm font-bold text-purple-900 mb-4 flex items-center gap-2">
                          <span className="text-lg">🚗</span>
                          Машины мэдээлэл
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-purple-700 font-medium block mb-1">Машины дугаар</label>
                            <p className="text-sm font-bold text-purple-900">{data.vehicle_number || '-'}</p>
                          </div>
                          <div>
                            <label className="text-xs text-purple-700 font-medium block mb-1">Машины төрөл</label>
                            <p className="text-sm font-bold text-purple-900">{data.vehicle_type || '-'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ============================================ */}
                {/* TAB 2: ХҮРГЭЛТҮҮД */}
                {/* ============================================ */}
                {activeTab === 'deliveries' && (
                  <div className="space-y-4">
                    {detailData?.recent_deliveries && detailData.recent_deliveries.length > 0 ? (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900">
                            Сүүлийн 10 хүргэлт
                          </h3>
                          <span className="text-sm text-gray-600">
                            Нийт: {data.total_deliveries || 0}
                          </span>
                        </div>
                        <div className="space-y-3">
                          {detailData.recent_deliveries.map((delivery) => (
                            <div
                              key={delivery.id}
                              className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h4 className="font-bold text-gray-900 mb-1">
                                    {delivery.delivery_number}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    {delivery.shop?.shop_name}
                                  </p>
                                </div>
                                {getDeliveryStatusBadge(delivery.status)}
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-600">Хүлээн авагч:</span>
                                  <p className="font-semibold text-gray-900">{delivery.customer_name}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Утас:</span>
                                  <p className="font-semibold text-gray-900">{delivery.customer_phone}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">COD дүн:</span>
                                  <p className="font-semibold text-gray-900">{delivery.cod_amount?.toLocaleString()}₮</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Огноо:</span>
                                  <p className="font-semibold text-gray-900">{formatDate(delivery.created_at)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📦</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          Хүргэлт байхгүй
                        </h3>
                        <p className="text-gray-600">
                          Энэ жолоочид хүргэлт хувиарлагдаагүй байна
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* ============================================ */}
                {/* TAB 3: СТАТИСТИК */}
                {/* ============================================ */}
                {activeTab === 'stats' && (
                  <div className="space-y-6">
                    {statsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600"></div>
                          <p className="text-gray-600 mt-4">Статистик уншиж байна...</p>
                        </div>
                      </div>
                    ) : stats ? (
                      <>
                        {/* Driver Info */}
                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                              {stats.driver.driver_name?.charAt(0)}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-blue-900">{stats.driver.driver_name}</h3>
                              <div className="flex items-center gap-4 mt-2">
                                {renderStars(parseFloat(stats.driver.rating) || 0)}
                                <span className="text-sm text-blue-700">
                                  {stats.driver.successful_deliveries} / {stats.driver.total_deliveries} амжилттай
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Period Stats */}
                        {stats.period_stats && (
                          <>
                            <h3 className="text-lg font-bold text-gray-900">Хугацааны статистик</h3>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                                <div className="text-3xl font-bold text-blue-600 mb-1">
                                  {stats.period_stats.total}
                                </div>
                                <div className="text-xs text-gray-600 font-medium">Нийт</div>
                              </div>

                              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                                <div className="text-3xl font-bold text-green-600 mb-1">
                                  {stats.period_stats.delivered}
                                </div>
                                <div className="text-xs text-gray-600 font-medium">Хүргэсэн</div>
                              </div>

                              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                                <div className="text-3xl font-bold text-red-600 mb-1">
                                  {stats.period_stats.cancelled}
                                </div>
                                <div className="text-xs text-gray-600 font-medium">Цуцлагдсан</div>
                              </div>

                              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                                <div className="text-3xl font-bold text-yellow-600 mb-1">
                                  {stats.period_stats.postponed}
                                </div>
                                <div className="text-xs text-gray-600 font-medium">Хойшлогдсон</div>
                              </div>
                            </div>

                            {/* Financial Stats */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-bold text-green-900">COD дүн</h4>
                                  <span className="text-2xl">💰</span>
                                </div>
                                <div className="text-2xl font-bold text-green-600">
                                  {(stats.period_stats.total_cod || 0).toLocaleString()}₮
                                </div>
                              </div>

                              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-bold text-blue-900">Хүргэлтийн төлбөр</h4>
                                  <span className="text-2xl">🚚</span>
                                </div>
                                <div className="text-2xl font-bold text-blue-600">
                                  {(stats.period_stats.total_delivery_fee || 0).toLocaleString()}₮
                                </div>
                              </div>

                              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-bold text-purple-900">Амжилтын хувь</h4>
                                  <span className="text-2xl">📊</span>
                                </div>
                                <div className="text-2xl font-bold text-purple-600">
                                  {stats.period_stats.success_rate || 0}%
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📊</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          Статистик байхгүй
                        </h3>
                        <p className="text-gray-600">
                          Энэ жолоочийн статистик мэдээлэл байхгүй байна
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Хаах
            </button>
            <button
              onClick={() => {
                onClose();
                onEdit();
              }}
              className="px-6 py-2.5 bg-gradient-to-r from-yellow-600 to-yellow-500 text-white font-semibold rounded-lg hover:from-yellow-700 hover:to-yellow-600 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <span className="text-lg">✏️</span>
              Засах
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDetailModal;